/**
 * ModeAccordion — Tank Redesign Editor variant A right-pane container.
 *
 * Source: design_handoff_tank/tank-redesign/screens-editor.jsx §
 * Screen04EditorA + the `Accordion` helper. Replaces the prior
 * Develop / IPTC tab strip in `ModePanel.jsx` with a stacked accordion
 * of four sections — Crop / Players · Faces / IPTC · Caption Builder / Develop.
 *
 * Each section is owned by `<ModeAccordionItem>` (sibling file) and
 * receives `open` from this parent based on the active key. Only one
 * section is open at a time. Keyboard `1`/`2`/`3`/`4` jumps between
 * sections (handled by the parent page via `useEditKeyboardShortcuts`).
 */

import { Children, cloneElement, isValidElement, useState } from 'react';

import styles from './ModeAccordion.module.css';

/**
 * @param {Object} props
 * @param {string} [props.defaultActive]   — initial open key
 * @param {string} [props.active]          — controlled open key (overrides default)
 * @param {(key: string) => void} [props.onActiveChange]
 * @param {React.ReactNode} props.children — `<ModeAccordionItem>`s in display order
 */
export default function ModeAccordion({
  defaultActive,
  active: controlledActive,
  onActiveChange,
  children,
}) {
  const [uncontrolledActive, setUncontrolledActive] = useState(defaultActive);
  const isControlled = controlledActive !== undefined;
  const activeKey = isControlled ? controlledActive : uncontrolledActive;

  const setActive = (key) => {
    if (!isControlled) setUncontrolledActive(key);
    onActiveChange?.(key);
  };

  return (
    <div className={styles.accordion}>
      {Children.map(children, (child) => {
        if (!isValidElement(child)) return child;
        const isOpen = child.props.itemKey === activeKey;
        return cloneElement(child, {
          open: isOpen,
          onToggle: () => setActive(isOpen ? null : child.props.itemKey),
        });
      })}
    </div>
  );
}
